import { allColumns, ColumnKey } from '~/constants/people';
import { CustomFieldType } from '~/types/api.graphql';

import { parseKeptField } from './schemas';
import { exceedsSubmissionValueLimit } from './submission';
import {
  CUSTOMER_LIST_KEPT_COLUMNS_KIND,
  CustomerListColumn,
  CustomerListKeptColumnsValue,
  CustomerListKeptField,
} from './types';

const CUSTOM_FIELD_NAME_REGEX = /^[a-zA-Z0-9_-]+$/;

// Names the platform's upload form refuses for a kept column: every people column and column key
const RESTRICTED_CUSTOM_FIELD_NAMES = new Set(
  [...allColumns, ...Object.values(ColumnKey)].map((name) => `${name}`.toLowerCase()),
);

/**
 * Whether a kept column may be saved under `name`: ASCII letters, digits, `_` and `-` only, not a restricted name, and
 * not one of `takenNames`, ignoring case. Reserved names and the per-type limits are the API's checks on creation.
 */
export const isValidCustomFieldName = (name: string, takenNames: string[]) =>
  hasValidCustomFieldNameFormat(name) &&
  !isRestrictedCustomFieldName(name) &&
  !takenNames.some((taken) => taken.trim().toLowerCase() === name.trim().toLowerCase());

const hasValidCustomFieldNameFormat = (name: string) => CUSTOM_FIELD_NAME_REGEX.test(name.trim());

const isRestrictedCustomFieldName = (name: string) => RESTRICTED_CUSTOM_FIELD_NAMES.has(name.trim().toLowerCase());

/** One row of the **Keep extra columns** section. */
export type KeptColumnDraft = {
  keep: boolean;
  savedName: string;
  fieldType: CustomFieldType;
};

export enum KeptColumnsProblem {
  InvalidName = 'invalidName',
  RestrictedName = 'restrictedName',
  DuplicateName = 'duplicateName',
  TooLongToSend = 'keptColumnsTooLongToSend',
}

export type KeptColumnsIssue = { problem: KeptColumnsProblem; columnIndex?: number };

/** Rows start unkept, saved under the column's name, as the suggested type or `String`, as on the platform. */
export const defaultKeptColumnDraft = ({ columnName, suggestedFieldType }: CustomerListColumn): KeptColumnDraft => ({
  keep: false,
  savedName: columnName,
  fieldType: suggestedFieldType ?? CustomFieldType.String,
});

export const buildKeptColumnsValue = (
  attachmentId: string,
  columns: CustomerListColumn[],
  drafts: Partial<Record<number, KeptColumnDraft>>,
) => {
  const value: CustomerListKeptColumnsValue = {
    kind: CUSTOMER_LIST_KEPT_COLUMNS_KIND,
    attachmentId,
    fields: columns.flatMap(({ index, columnName }): CustomerListKeptField[] => {
      const draft = drafts[index];
      return draft?.keep ? [[index, columnName, draft.savedName.trim(), draft.fieldType]] : [];
    }),
  };
  return value;
};

/** Every reason the kept columns can't be submitted, empty when they can. */
export const findKeptColumnsIssues = (
  attachmentId: string,
  columns: CustomerListColumn[],
  drafts: Partial<Record<number, KeptColumnDraft>>,
): KeptColumnsIssue[] => {
  const kept = columns.filter(({ index }) => drafts[index]?.keep);
  const issues = kept.flatMap(({ index }): KeptColumnsIssue[] => {
    const savedName = drafts[index]?.savedName ?? '';
    if (!hasValidCustomFieldNameFormat(savedName))
      return [{ problem: KeptColumnsProblem.InvalidName, columnIndex: index }];
    if (isRestrictedCustomFieldName(savedName)) {
      return [{ problem: KeptColumnsProblem.RestrictedName, columnIndex: index }];
    }
    const earlierNames = kept
      .slice(
        0,
        kept.findIndex((column) => column.index === index),
      )
      .map((column) => drafts[column.index]?.savedName.trim() ?? '');
    return isValidCustomFieldName(savedName, earlierNames)
      ? []
      : [{ problem: KeptColumnsProblem.DuplicateName, columnIndex: index }];
  });
  if (exceedsSubmissionValueLimit(JSON.stringify(buildKeptColumnsValue(attachmentId, columns, drafts)))) {
    issues.push({ problem: KeptColumnsProblem.TooLongToSend });
  }
  return issues;
};

/**
 * The rows a form starts from: a starting or earlier submitted value, over the defaults. A field counts only when it is
 * `[index, columnName, savedName, type]` and its index and name are an inspected column's; any other is left out on
 * its own.
 */
export const readKeptColumnsValue = (value: { fields: unknown[] }, columns: CustomerListColumn[]) => {
  const fields = value.fields.flatMap((rawField) => {
    const field = parseKeptField(rawField);
    const isInspected = columns.some(({ index, columnName }) => index === field?.[0] && columnName === field?.[1]);
    return field && isInspected ? [field] : [];
  });
  return Object.fromEntries(
    columns.map((column) => {
      const field = fields.find(([index]) => index === column.index);
      const draft: KeptColumnDraft = field
        ? { keep: true, savedName: field[2], fieldType: field[3] }
        : defaultKeptColumnDraft(column);
      return [column.index, draft];
    }),
  ) as Record<number, KeptColumnDraft>;
};
